import {
    ArrowTrendingUpIcon,
    GlobeAltIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MarketOpportunity = () => {
  const { t } = useTranslation();

  const marketStats = [
    {
      icon: GlobeAltIcon,
      value: t('marketOpportunity.stats.export.value'),
      label: t('marketOpportunity.stats.export.label'),
      description: t('marketOpportunity.stats.export.description'),
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
    {
      icon: ArrowTrendingUpIcon,
      value: t('marketOpportunity.stats.online.value'),
      label: t('marketOpportunity.stats.online.label'),
      description: t('marketOpportunity.stats.online.description'),
      color: 'from-avoprime-light to-green-400',
    },
    {
      icon: UserGroupIcon,
      value: t('marketOpportunity.stats.target.value'),
      label: t('marketOpportunity.stats.target.label'),
      description: t('marketOpportunity.stats.target.description'),
      color: 'from-avoprime-secondary to-avoprime-primary',
    },
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section className='py-8 bg-gradient-to-br from-white to-avoprime-light/5'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className='text-center mb-16'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6'>
            {t('marketOpportunity.title')}
          </h2>
          <p className='text-xl lg:text-2xl font-montserrat text-gray-700 max-w-4xl mx-auto leading-relaxed'>
            {t('marketOpportunity.subtitle')}
          </p>
        </motion.div>

        {/* Market Stats */}
        <motion.div
          variants={containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-16'
        >
          {marketStats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className='text-center h-64 flex flex-col justify-center'
            >
              <div
                className={`w-24 h-24 bg-gradient-to-br ${stat.color} rounded-full flex items-center justify-center mx-auto mb-6`}
              >
                <stat.icon className='w-12 h-12 text-white' />
              </div>
              <div className='text-4xl lg:text-5xl font-eras font-bold text-avoprime-primary mb-2'>
                {stat.value}
              </div>
              <div className='text-xl font-eras font-bold text-avoprime-secondary mb-2'>
                {stat.label}
              </div>
              <p className='text-gray-600 font-montserrat flex-1 flex items-center justify-center'>
                {stat.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Export Focus */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className='bg-gradient-to-r from-avoprime-primary to-avoprime-secondary rounded-3xl p-8 lg:p-12 text-white mb-16'
        >
          <div className='grid lg:grid-cols-2 gap-8 items-center'>
            <div>
              <h3 className='text-3xl lg:text-4xl font-eras font-bold mb-6'>
                {t('marketOpportunity.exportFocus.title')}
              </h3>
              <p className='text-lg lg:text-xl font-montserrat opacity-90 mb-6 leading-relaxed'>
                {t('marketOpportunity.exportFocus.description')}
              </p>
              <div className='flex flex-wrap gap-4'>
                <div className='bg-white/20 px-4 py-2 rounded-full text-sm font-montserrat'>
                  {t('marketOpportunity.exportFocus.markets.europe')}
                </div>
                <div className='bg-white/20 px-4 py-2 rounded-full text-sm font-montserrat'>
                  {t('marketOpportunity.exportFocus.markets.southAmerica')}
                </div>
              </div>
            </div>
            <div className='relative'>
              <div
                className='w-full h-64 lg:h-80 rounded-2xl bg-cover bg-center'
                style={{
                  backgroundImage: "url('/avocados.webp')",
                }}
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl' />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MarketOpportunity;
